// Import React dependencies.
import React, {Fragment, FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react'
// Import the Slate editor factory.
import {
    createEditor,
    Editor,
    Text,
    Transforms,
    Element as SlateElement,
    Descendant,
    Node as SlateNode,
    Range, NodeEntry, BaseEditor, Path, BaseOperation
} from 'slate'

// Import the Slate components and React plugin.
import {Slate, Editable, withReact, ReactEditor, RenderElementProps} from 'slate-react';
import {withHistory} from 'slate-history';
import classNames from "classnames";
import {
    DeserializedElement,
    DeserializedElementRule,
    DeserializedLeafRule, DeserializedNode,
    DeserializedRules, RenderElement
} from "./slate/Transformation";
import {
    exportToHtml,
    importFromHtml,
} from "./slate/html/Html";
import debounce from "../../../utils/debounce";
import Toolbar from "../toolbar";
import useSelection from "./slate/useSelection";
import {element, ReactComponentLike} from "prop-types";
import HtmlElements from "./plugins/html/index";
import Button from "../Button";
import {hotKeyAction} from "./slate/utils/hotkey";
import {isKeyHotkey} from "is-hotkey";
import {RenderLeafProps} from "slate-react/dist/components/editable";

const fullToolbar = ['bold', 'italic', 'underline', 'strike', 'figure', /*'align-left', 'align-center', 'align-right',*/ 'link', 'unlink', 'video', 'Poll', 'OptInForm', /*'lists', */'header', 'blockquote', 'full-screen'];
const defaultToolbar = ['bold', 'italic', 'underline', 'strike', 'figure', /*'align-left', 'align-center', 'align-right',*/ 'link', 'unlink', 'video', 'Poll', 'OptInForm', /*'lists', */'header', 'blockquote', 'full-screen'];
const emptyToolbar: [] = [];

const withRuleTypes = <T,>(editor: BaseEditor) => {
    const {isInline, isVoid} = editor;
    let rules: DeserializedRules<T> = {
        elements: {},
        leaves: {}
    };
    editor.isInline = (element: SlateElement) => {
        let keys = Object.keys(rules.elements);
        for (let i = 0;i < keys.length;i++) {
            if (rules.elements[keys[i]].match(element)) {
                return rules.elements[keys[i]].tagType(element) === 'inline';
            }
        }
        keys = Object.keys(rules.leaves);
        for (let i = 0;i < keys.length;i++) {
            if (rules.leaves[keys[i]].match(element)) {
                return rules.leaves[keys[i]].tagType(element) === 'inline';
            }
        }
        return isInline(element);
    }
    editor.isVoid = (element: SlateElement) => {
        let keys = Object.keys(rules.elements);
        for (let i = 0;i < keys.length;i++) {
            if (rules.elements[keys[i]].match(element)) {
                return rules.elements[keys[i]].isVoid(element) === true;
            }
        }
        keys = Object.keys(rules.leaves);
        for (let i = 0;i < keys.length;i++) {
            if (rules.leaves[keys[i]].match(element)) {
                return rules.leaves[keys[i]].isVoid(element) === true;
            }
        }
        return isVoid(element);
    }
    // @ts-ignore
    editor.setRules = (newRules: DeserializedRules<T>) => {
        rules = newRules;
    }
    return editor;
}

const withInsert = (editor: ReactEditor) => {
    // @ts-ignore
    const {insertData} = editor;
    let onPaste: (data: DataTransfer, insertData: Function) => void;
    // @ts-ignore
    editor.insertData = (data: DataTransfer) => {
        /*const html = data.getData('text/html');
        console.log(html);
        if (html) {
            const parsed = new DOMParser().parseFromString(html, 'text/html')
            const fragment = deserialize(parsed.body)
            Transforms.insertFragment(editor, fragment)
            return
        }*/
        if (onPaste) {
            onPaste(data, insertData);
        } else {
            const text = data.getData('text/plain');
            if (text) {
                Transforms.insertFragment(editor, [{text}]);
                return
            }
            insertData(data)
        }
    }
    // @ts-ignore
    editor.onPaste = (on: (data: DataTransfer, insertData: Function) => void) => {
        onPaste = on;
    }
    return editor;
}

const withNormalize = (editor: ReactEditor) => {
    const { normalizeNode } = editor;

    type F = (args: {entry: NodeEntry, normalizeNode: (entry: NodeEntry, options?: {operation?: BaseOperation}) => void}) => void;

    let normalizer: F;

    editor.normalizeNode = (entry: NodeEntry) => {
        if (normalizer) {
            normalizer({entry, normalizeNode});
        } else {
            normalizeNode(entry);
        }
    }

    // @ts-ignore
    editor.setNormalizer = (normalizerFunction?: F) => {
        normalizer = normalizerFunction;
    }

    return editor
}


type Props<T> = {
    elements?: DeserializedRules<T>,
    toolbar?: string[]|ReactComponentLike[],
    name?: string,
    label?: any,
    className?: string|object,
    height?: number|string,
    disabled?: boolean,
    required?: boolean,
    value?: any,
    defaultValue?: any,
    import?: (value: any, rules: DeserializedRules<T>) => any
    export?: (value: any, rules: DeserializedRules<T>) => any,
    saveChanges?: (field: string, value: any) => void,
    onPaste?: (data: DataTransfer) => any,
    reader?: boolean,
};

const makeid = (length: number) => {
    var result           = '';
    var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for ( var i = 0; i < length; i++ ) {
        result += characters.charAt(Math.floor(Math.random() *
            charactersLength));
    }
    return result;
}

let i = 0;
const initialValue = () => (new Date()).getTime() + "_" + makeid(10) + "_" + (++i);

const SlateEditor = <T,>(props: Props<T>) => {
    const editorProps = {...props};
    const [fullScreen, setFullScreen] = useState(false);
    const [editor] = useState(() => withNormalize(withInsert(withReact(withRuleTypes<T>(withHistory(createEditor()))))));
    const [selection, setSelection] = useSelection(editor);
    // Render the Slate context.
    const [value, setValue] = useState(initialValue);

    const editorId = useMemo(() => "slate-" + new Date().getTime(), []);

    const elements: DeserializedRules<T> = useMemo(() => {
        // @ts-ignore
        const elements: DeserializedRules<T> = {...(props.elements || HtmlElements)};
        // @ts-ignore
        elements.elements = {
            ...(elements.elements || {}),
            editorId: {
                isVoid: () => false,
                tagType: () => 'block',
                match: (element: DeserializedElement) => element.type === editorId,
                deserialize(el, next) {
                    return undefined;
                },
                serialize(node: DeserializedElement, next) {
                    if (node.type === editorId) {
                        return next(node.children);
                    }
                },
                render: (props) => {
                    const {node} = props;
                    return node.element?.type === editorId ? <div {...node.attributes}>{node.children}</div> : undefined;
                }
            }
        } as {[key: string]: DeserializedElementRule<T>}
        return elements;
    }, [props.elements, editorId]);
    editorProps.elements = elements;
    useEffect(() => {
        // @ts-ignore
        editor.setRules(elements);
    }, [elements]);

    const onPaste = useCallback((data, insertData) => {
        if (props.onPaste) {
            data = props.onPaste(data);
            if (data) {
                insertData(data);
            }
        } else {
            data = (props.import || importFromHtml)(data, elements as DeserializedRules<any>);
            data = data.reduce((all: any[], item: any) => {
                if (item?.type?.indexOf("slate-") === 0) {
                    return [
                        ...all,
                        ...item.children
                    ]
                } else {
                    return [...all, item];
                }
            }, []);
            //console.log(JSON.parse(JSON.stringify(data)));
            Transforms.insertFragment(editor, data);
        }
    }, [elements])

    // @ts-ignore
    editor.onPaste(onPaste);

    const normalizer = useCallback(({entry, normalizeNode}: {entry?: NodeEntry, normalizeNode?: (entry: NodeEntry) => void} = {}) => {
        let normalized = false;
        Editor.withoutNormalizing(editor, () => {
            let elementKeys = Object.keys(elements.elements);
            for (let i = 0; i < elementKeys.length; i++) {
                if (elements.elements[elementKeys[i]]?.match(entry[0]) && elements.elements[elementKeys[i]]?.normalize) {
                    elements.elements[elementKeys[i]].normalize({entry, normalizeNode, editor});
                    normalized = true;
                    return;
                }
            }
            let leafKeys = Object.keys(elements.leaves);
            for (let i = 0; i < leafKeys.length; i++) {
                if (elements.leaves[elementKeys[i]]?.match(entry[0]) && elements.leaves[elementKeys[i]]?.normalize) {
                    elements.leaves[elementKeys[i]].normalize({entry, normalizeNode, editor});
                    normalized = true;
                    return;
                }
            }
        });
        if (!normalized) {
            normalizeNode(entry);
        }
    }, [elements]);

    // @ts-ignore
    editor.setNormalizer(normalizer);

    let propValue = useMemo(
        () => {
            if (props.value !== value) {
                let content;
                content = (props.import || importFromHtml)(props.value ?? props.defaultValue, elements as DeserializedRules<any>);
                Transforms.deselect(editor);
                // @ts-ignore
                editor.children = [{type: editorId, children: content}];
                setValue(props.value);
                return content;
            } else {
                return editor.children;
            }
        },
        [props.value, props.import, elements]
    );

    const saveChanges = useCallback(debounce((value: any) => {
        //console.log(JSON.parse(JSON.stringify(propValue)));
        //console.log(JSON.parse(JSON.stringify(value)));
        // Save the value to Local Storage.
        //console.log(JSON.parse(JSON.stringify(value)));
        value = (props.export || exportToHtml)(value, elements as DeserializedRules<any>);
        setValue(value);
        props.saveChanges && props.saveChanges(props.name, value);
    }, 300), []);

    editorProps.saveChanges = saveChanges;

    // Define a rendering function based on the element passed to `props`. We use
    // `useCallback` here to memoize the function for subsequent renders.
    const renderElement: (props: RenderElementProps) => JSX.Element = useCallback(props => {
        const render = (node: RenderElement, rules: {[key: string]: DeserializedElementRule<T>}) => {
            let keys = Object.keys(rules);
            for (let i = 0; i < keys.length; i++) {
                let v = rules[keys[i]]?.render({editor, node});
                if (v !== undefined) {
                    return v;
                }
            }
            return null;
        }
        return render(props, elements.elements);
    }, [elements]);

    // Define a leaf rendering function that is memoized with `useCallback`.
    const renderLeaf: (props: RenderLeafProps) => JSX.Element = useCallback(props => {
        const render = (node: RenderLeafProps, rules: {[key: string]: DeserializedLeafRule<T>}) => {
            let leaf: any = node.children;
            let keys = Object.keys(rules);
            for (let i = 0; i < keys.length; i++) {
                let next = rules[keys[i]]?.render({editor, node, leaf});
                if (next !== undefined) {
                    leaf = next;
                }
            }
            return <span {...node.attributes}>{leaf}</span>;
        }
        return render(props, elements.leaves);
    }, [])

    const onChange = useCallback(value => {
        setSelection(editor.selection);
        const isAstChange = editor.operations.some(
            op => 'set_selection' !== op.type
        )
        if (isAstChange) {
            saveChanges(value);
        }
    }, [props.saveChanges, props.export, elements]);

    const insertNode = useCallback(({type, props, children}: {type?: string, props?: object, children?: Descendant[]} = {}) => {
        children = children || [{ text: '' }];
        const newNode: SlateNode&{type: string} = {...props, type, children};
        Transforms.insertNodes(editor, newNode)
    }, []);

    let style: {display?: string} = {};
    if (props.label === false) {
        style.display = "none";
    }

    const toolbar = props.toolbar || defaultToolbar;
    editorProps.toolbar = toolbar;
    let Holder: ReactComponentLike = Fragment;
    let holderProps = {};
    if (fullScreen) {
        // TODO
        //Holder = Portal;
        //holderProps = {portalId: 'fullscreen'};
    }
    return (
        <Holder {...holderProps}>
            <label htmlFor={props.name} className="control-label" style={style}>{props.label} {props.required && <span className="invalid-feedback">*</span>}</label>
            <div className={classNames("rt-editor", {"full-screen": fullScreen}, props.className)}>
                {!props.reader && <Toolbar className="rt-editor-toolbar">
                    {Object.entries(elements.leaves).map(([name, leaf], key) => {
                        let buttonProps: {[key: string]: any} = {};
                        if (leaf.isActive) {
                            buttonProps.isActive = leaf.isActive(editor);
                            buttonProps.toggle = () => leaf.toggle(editor);
                        }
                        buttonProps.insert = insertNode;
                        // @ts-ignore
                        return toolbar.indexOf(name) !== -1 && leaf.toolbarButton && <leaf.toolbarButton
                            key={key}
                            {...editorProps}
                            selection={selection}
                            disabled={props.disabled}
                            toolbarContainerId={"rt-toolbar-" + editorId}
                            editor={editor}
                            {...buttonProps}
                        />
                    })}
                    {Object.entries(elements.elements).map(([name, element], key) => {
                        let buttonProps: {[key: string]: any} = {};
                        if (element.isActive) {
                            buttonProps.isActive = element.isActive(editor);
                            buttonProps.toggle = () => element.toggle(editor);
                        }
                        buttonProps.insert = insertNode;
                        // @ts-ignore
                        return toolbar.indexOf(name) !== -1 && element.toolbarButton && <element.toolbarButton
                            key={key}
                            {...editorProps}
                            selection={selection}
                            disabled={props.disabled}
                            toolbarContainerId={"rt-toolbar-" + editorId}
                            editor={editor}
                            {...buttonProps}
                        />
                    })}
                    {toolbar.map((toolbarButton, idx) => {
                        if (toolbarButton && typeof toolbarButton !== 'string') {
                            const Comp = toolbarButton;
                            let buttonProps: {[key: string]: any} = {};
                            buttonProps.insert = insertNode;
                            return <Comp
                                key={idx}
                                {...editorProps}
                                selection={selection}
                                disabled={props.disabled}
                                toolbarContainerId={"rt-toolbar-" + editorId}
                                editor={editor}
                                {...buttonProps}
                            />;
                        }
                    })}
                    {toolbar.indexOf('full-screen') !== -1 && <Button
                        tooltip="Teljes képernyős mód"
                        className="pull-right"
                        color="default"
                        onClick={() => setFullScreen(!fullScreen)}
                        icon={{lib: "fas", name: "compress"}}
                    />}
                </Toolbar>}
                <div id={"rt-toolbar-" + editorId} />
                <div className="rt-editor-input" style={{height: props.height}}>
                    <Slate
                        editor={editor}
                        value={propValue}
                        onChange={onChange}
                    >
                        <Editable
                            readOnly={props.disabled}
                            renderElement={renderElement}
                            renderLeaf={renderLeaf}
                            onKeyUp={(event) => {
                                let all = [...Object.values(elements.elements), ...Object.values(elements.leaves)];
                                for (let i = 0;i < all.length;i++) {
                                    if (all[i].onKeyUp) {
                                        if (all[i].onKeyUp(event, editor) === false) {
                                            break;
                                        }
                                    }
                                }
                            }}
                            onKeyDown={(event) => {
                                let all = [...Object.values(elements.elements), ...Object.values(elements.leaves)];
                                for (let i = 0;i < all.length;i++) {
                                    if (all[i].onKeyDown) {
                                        if (all[i].onKeyDown(event, editor) === false) {
                                            return false;
                                        }
                                    }
                                }
                                let elementKeys = Object.keys(elements.elements);
                                for (let i = 0;i < elementKeys.length;i++) {
                                    if (elements.elements[elementKeys[i]].hotKey && hotKeyAction(elements.elements[elementKeys[i]].hotKey)(event)) {
                                        event.preventDefault();
                                        elements.elements[elementKeys[i]].toggle(editor);
                                        return false;
                                    }
                                }
                                let leafKeys = Object.keys(elements.leaves);
                                for (let i = 0;i < leafKeys.length;i++) {
                                    if (elements.leaves[leafKeys[i]].hotKey && hotKeyAction(elements.leaves[leafKeys[i]].hotKey)(event)) {
                                        event.preventDefault();
                                        elements.leaves[leafKeys[i]].toggle(editor);
                                        return false;
                                    }
                                }
                                // browser shift enter add a linebreak
                                if (isKeyHotkey('shift+enter')(event)) {
                                    event.preventDefault();
                                    return false;
                                }
                            }}
                        />
                    </Slate>
                </div>
            </div>
        </Holder>
    );
}

const EditorField = <T,>(props: Props<T>) => {
    //let [value, setValue] = useState("<p>asdfjahdfjlkadhgkha kdka dfaksdf kaasfd</p>");
    return <SlateEditor {...props} />;
};

export default EditorField;

export {EditorField, defaultToolbar, emptyToolbar, fullToolbar};